import {
  ChatCircleText,
  Check,
  Clock,
  Copy,
  Link as LinkIcon,
  PaperPlaneTilt,
  X,
  Warning,
} from "@phosphor-icons/react";
import ConfirmDialog from "../../../ui/ConfirmDialog";
import MessagingStatusBadge from "../../../ui/MessagingStatusBadge";

// Human-readable reason a scheduled SMS terminally did not go out — written on
// the appointment doc by the Cloud Task handlers.
const smsOutcomeNotice = (outcome) => {
  switch (outcome) {
    case "skipped_quota":
      return "ההודעה לא נשלחה — מכסת ה-SMS החודשית נוצלה במלואה";
    case "skipped_declined":
      return "התזכורת לא נשלחה — המטופל/ת השיב/ה שלא יגיע/תגיע";
    case "skipped_no_phone":
      return "ההודעה לא נשלחה — לא נמצא מספר טלפון למטופל/ת";
    case "skipped_inactive":
      return "ההודעה לא נשלחה — המטופל/ת בארכיון";
    case "skipped_past":
      return "ההודעה לא נשלחה — מועד הפגישה חלף";
    case "skipped_opt_out":
      return "ההודעה לא נשלחה — המטופל/ת ביקש/ה להסיר אותו/ה מהודעות אוטומטיות";
    case "skipped_disabled_by_niv":
      return "ההודעה לא נשלחה — סוג הודעה זה כבוי כרגע במערכת";
    case "failed_no_link":
    case "failed_provider":
    case "failed_internal":
      return "שליחת ההודעה נכשלה — צוות קליניקיט קיבל התראה על כך";
    default:
      return null;
  }
};

const STATUS_LABEL = {
  confirmed: "אישר/ה הגעה",
  declined: "ביטל/ה הגעה",
  pending: "ממתין לתשובה",
};

function SmsRow({ label, entry, smsType, form, stamp }) {
  const {
    canCancelScheduledSms,
    setCancelSmsTarget,
    cancellingSmsType,
    copyLink,
    copiedLinkId,
  } = form;

  const stateChip = entry.sent ? (
    <span className={`apt-wiz-sms-chip ${entry.status}`}>
      {entry.status === "pending" ? "נשלח — ממתין" : STATUS_LABEL[entry.status]}
    </span>
  ) : entry.scheduled ? (
    <span className="apt-wiz-sms-chip scheduled">
      <Clock size={11} weight="bold" />
      מתוזמן
      {entry.scheduledAt
        ? ` ל-${entry.scheduledAt.slice(8, 10)}/${entry.scheduledAt.slice(5, 7)} ${entry.scheduledAt.slice(11, 16)}`
        : ""}
    </span>
  ) : (
    <span className="apt-wiz-sms-chip none">לא נשלח</span>
  );

  return (
    <div className="apt-wiz-sms-status-row">
      <span className="apt-wiz-sms-status-label">{label}</span>
      {stateChip}
      {entry.sent && stamp?.channel && <MessagingStatusBadge messaging={stamp} />}
      <span className="apt-wiz-sms-status-actions">
        {entry.linkId && (
          <button
            type="button"
            className="apt-wiz-series-btn"
            title="העתקת קישור אישור"
            onClick={() => copyLink(entry.linkId)}
          >
            {copiedLinkId === entry.linkId ? (
              <Check size={13} weight="bold" />
            ) : (
              <Copy size={13} />
            )}
          </button>
        )}
        {canCancelScheduledSms(smsType) && (
          <button
            type="button"
            className="apt-wiz-series-btn cancel"
            title="ביטול ההודעה המתוזמנת"
            onClick={() => setCancelSmsTarget({ type: smsType })}
            disabled={cancellingSmsType === smsType}
          >
            <X size={13} weight="bold" />
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Edit-summary communication panel: confirmation-SMS status (live), queued-SMS
 * cancel, confirmation-link copy/create, late "send SMS now" flow.
 */
function CommunicationPanel({ form }) {
  const {
    isEditing,
    confirmationStatus,
    smsAppointment,
    getPatientPhone,
    handleCreateLink,
    canSendLateSms,
    lateSmsSeriesAvailable,
    lateSmsFutureOccurrences,
    lateSmsReminderOffered,
    lateSmsPreviewText,
    showLateSmsDialog,
    setShowLateSmsDialog,
    lateSmsScope,
    setLateSmsScope,
    lateSmsIncludeReminder,
    setLateSmsIncludeReminder,
    lateSmsSending,
    handleLateSmsSend,
    cancelSmsTarget,
    setCancelSmsTarget,
    handleConfirmCancelScheduledSms,
    confirmationLinks,
    // manual reminder now
    canSendReminderNow,
    reminderNowAutoPending,
    reminderNowLastSentAt,
    showReminderNowDialog,
    setShowReminderNowDialog,
    reminderNowSending,
    handleSendReminderNow,
  } = form;

  if (!isEditing || !confirmationStatus) return null;
  const patientPhone = getPatientPhone();

  const outcomeText =
    smsOutcomeNotice(smsAppointment?.initialSmsOutcome) ||
    smsOutcomeNotice(smsAppointment?.reminderSmsOutcome);

  const manualCount = smsAppointment?.manualReminderCount || 0;
  const showManualRow = manualCount > 0 || canSendReminderNow;

  const showAnything =
    patientPhone ||
    confirmationLinks.length > 0 ||
    canSendLateSms ||
    showManualRow;
  if (!showAnything) return null;

  const manualSentLabel = reminderNowLastSentAt
    ? `נשלחה ${reminderNowLastSentAt.toLocaleDateString("he-IL", {
        day: "2-digit",
        month: "2-digit",
      })} ${reminderNowLastSentAt.toLocaleTimeString("he-IL", {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    : "נשלחה";

  return (
    <div className="apt-wiz-comm-panel">
      <div className="apt-wiz-series-overview-header">
        <ChatCircleText size={15} weight="fill" />
        <span>תקשורת עם המטופל</span>
      </div>

      <SmsRow
        label="אישור הגעה"
        entry={confirmationStatus.initial}
        smsType="initial"
        form={form}
        stamp={smsAppointment?.messagingInitial}
      />
      <SmsRow
        label="תזכורת"
        entry={confirmationStatus.reminder}
        smsType="reminder"
        form={form}
        stamp={smsAppointment?.messagingReminder}
      />

      {showManualRow && (
        <div className="apt-wiz-sms-status-row">
          <span className="apt-wiz-sms-status-label">תזכורת ידנית</span>
          {manualCount > 0 ? (
            <span className="apt-wiz-sms-chip manual">
              <PaperPlaneTilt size={11} weight="bold" />
              {manualSentLabel}
              {manualCount > 1 ? ` (×${manualCount})` : ""}
            </span>
          ) : (
            <span className="apt-wiz-sms-chip none">לא נשלחה</span>
          )}
          {manualCount > 0 && smsAppointment?.messagingManualReminder?.channel && (
            <MessagingStatusBadge
              messaging={smsAppointment.messagingManualReminder}
            />
          )}
        </div>
      )}

      {outcomeText && (
        <div className="apt-wiz-hint-card warning">
          <Warning size={14} weight="fill" />
          <span>{outcomeText}</span>
        </div>
      )}

      {canSendReminderNow && (
        <button
          type="button"
          className="apt-wiz-fastpath-btn"
          onClick={() => setShowReminderNowDialog(true)}
          disabled={reminderNowSending}
        >
          <PaperPlaneTilt size={15} weight="fill" />
          {reminderNowSending ? "שולח תזכורת..." : "שלח תזכורת עכשיו"}
        </button>
      )}

      {!confirmationStatus.initial.linkId && patientPhone && !canSendLateSms && (
        <button
          type="button"
          className="apt-wiz-sms-details-toggle"
          onClick={() => handleCreateLink("initial")}
        >
          <LinkIcon size={13} />
          יצירת קישור אישור הגעה
        </button>
      )}

      {canSendLateSms && (
        <button
          type="button"
          className="apt-wiz-fastpath-btn"
          onClick={() => {
            setLateSmsScope("single");
            setLateSmsIncludeReminder(false);
            setShowLateSmsDialog(true);
          }}
        >
          <PaperPlaneTilt size={15} weight="fill" />
          שליחת SMS אישור הגעה עכשיו
        </button>
      )}

      {/* Late-SMS dialog */}
      {showLateSmsDialog && (
        <div
          className="apt-wiz-dialog-overlay"
          onClick={() => !lateSmsSending && setShowLateSmsDialog(false)}
        >
          <div className="apt-wiz-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="apt-wiz-dialog-header">
              <PaperPlaneTilt size={22} weight="bold" />
              <h3>שליחת SMS למטופל</h3>
            </div>

            {lateSmsSeriesAvailable && (
              <div className="apt-wiz-scope-options">
                <label className="apt-wiz-scope-option">
                  <input
                    type="radio"
                    name="wizLateSmsScope"
                    value="single"
                    checked={lateSmsScope === "single"}
                    onChange={() => setLateSmsScope("single")}
                  />
                  <span className="apt-wiz-scope-label">
                    מפגש זה בלבד
                    <span className="apt-wiz-scope-hint">
                      הודעת אישור הגעה לפגישה הנוכחית
                    </span>
                  </span>
                </label>
                <label className="apt-wiz-scope-option">
                  <input
                    type="radio"
                    name="wizLateSmsScope"
                    value="series"
                    checked={lateSmsScope === "series"}
                    onChange={() => setLateSmsScope("series")}
                  />
                  <span className="apt-wiz-scope-label">
                    כל הסדרה
                    <span className="apt-wiz-scope-hint">
                      הודעת סיכום אחת על {lateSmsFutureOccurrences.length} המפגשים
                      הקרובים
                    </span>
                  </span>
                </label>
              </div>
            )}

            {lateSmsReminderOffered && (
              <label className="apt-wiz-dialog-checkbox">
                <input
                  type="checkbox"
                  checked={lateSmsIncludeReminder}
                  onChange={(e) => setLateSmsIncludeReminder(e.target.checked)}
                />
                <span>לתזמן גם הודעת תזכורת לפני הפגישה</span>
              </label>
            )}

            <div className="apt-wiz-sms-message">{lateSmsPreviewText}</div>

            <div className="apt-wiz-dialog-actions">
              <button
                type="button"
                className="apt-wiz-btn secondary"
                onClick={() => setShowLateSmsDialog(false)}
                disabled={lateSmsSending}
              >
                ביטול
              </button>
              <button
                type="button"
                className="apt-wiz-btn primary"
                onClick={handleLateSmsSend}
                disabled={lateSmsSending}
              >
                {lateSmsSending ? "שולח..." : "שליחה"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual "send reminder now" confirm */}
      <ConfirmDialog
        isOpen={showReminderNowDialog}
        onClose={() => setShowReminderNowDialog(false)}
        onConfirm={handleSendReminderNow}
        title="שליחת תזכורת למטופל"
        message={
          "תישלח עכשיו תזכורת לפגישה עם קישור לאישור הגעה. " +
          "ההודעה תישלח בוואטסאפ, או ב-SMS אם אין וואטסאפ." +
          (reminderNowAutoPending
            ? " התזכורת האוטומטית המתוזמנת תישלח כרגיל."
            : "")
        }
        confirmText="שלח תזכורת"
        cancelText="ביטול"
        type="info"
        confirmDisabled={reminderNowSending}
      />

      {/* Cancel queued SMS confirm */}
      <ConfirmDialog
        isOpen={!!cancelSmsTarget}
        onClose={() => setCancelSmsTarget(null)}
        onConfirm={handleConfirmCancelScheduledSms}
        title="ביטול הודעה מתוזמנת"
        message={
          cancelSmsTarget?.type === "initial"
            ? "הודעת אישור ההגעה המתוזמנת תבוטל ולא תישלח למטופל. להמשיך?"
            : "הודעת התזכורת המתוזמנת תבוטל ולא תישלח למטופל. להמשיך?"
        }
        confirmText="בטל את ההודעה"
        cancelText="השאר"
        type="warning"
      />
    </div>
  );
}

export default CommunicationPanel;
